import { useState, useEffect } from 'react';
import { createApiClient } from '../../../services/ApiClient.jsx';

export const useTopicProgress = (accessToken, topics) => {
    const [topicProgress, setTopicProgress] = useState({});
    const [loading, setLoading] = useState(false);

    const apiClient = createApiClient(accessToken);

    const fetchTopicProgress = async () => {
        if (!topics || topics.length === 0) return;
        
        setLoading(true);
        try {
            const progressData = {};
            
            // Fetch submission history for each topic that has a form
            await Promise.all(
                topics
                    .filter(topic => topic.has_form) // Only check topics with forms
                    .map(async (topic) => {
                        try {
                            // We need to get the form ID from the topic/form endpoint
                            const formResponse = await apiClient.get(`/api/courses/${topic.course_id}/topics/${topic.id}/form`);
                            if (formResponse?.id) {
                                const historyResponse = await apiClient.get(`/api/submissions/history/${formResponse.id}`);
                                progressData[topic.id] = {
                                    totalAttempts: historyResponse.total_attempts || 0,
                                    submissions: historyResponse.submissions || [],
                                    hasCompletedSubmission: historyResponse.submissions?.some(s => s.status === 'completed') || false,
                                    bestScore: historyResponse.submissions?.length > 0 ? 
                                        Math.max(...historyResponse.submissions
                                            .filter(s => s.percentage !== null)
                                            .map(s => s.percentage) || [0]
                                        ) : 0
                                };
                            } else {
                                // Topic has no form yet
                                progressData[topic.id] = {
                                    totalAttempts: 0,
                                    submissions: [],
                                    hasCompletedSubmission: false,
                                    bestScore: 0
                                };
                            }
                        } catch (err) {
                            // If we can't fetch progress for this topic, set default values
                            console.warn(`Could not fetch progress for topic ${topic.id}:`, err);
                            progressData[topic.id] = {
                                totalAttempts: 0,
                                submissions: [],
                                hasCompletedSubmission: false,
                                bestScore: 0
                            };
                        }
                    })
            );
            
            setTopicProgress(progressData);
        } catch (err) {
            console.error('Error fetching topic progress:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accessToken && topics?.length > 0) {
            fetchTopicProgress();
        }
    }, [accessToken, topics]);

    return {
        topicProgress,
        loading,
        fetchTopicProgress
    };
};
